import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Loader2 } from "lucide-react";

interface PlanFeature {
  id: string;
  plan_id: string;
  feature_key: string;
  enabled: boolean;
  limit_value: number | null;
  created_at: string;
}

interface Plan {
  id: string;
  tier: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  trial_days: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  plan_features: PlanFeature[];
}

const FEATURE_LABELS: Record<string, string> = {
  max_checking_accounts: "Contas correntes",
  max_savings_accounts: "Contas poupan\u00e7a",
  max_credit_cards: "Cart\u00f5es de cr\u00e9dito",
  credit_card_access: "Acesso a cart\u00f5es",
  transfers_access: "Transfer\u00eancias",
  statement_import: "Importa\u00e7\u00e3o de extratos",
  budget_planning: "Planejamento or\u00e7ament\u00e1rio",
  provisions: "Provisionamento",
  max_goals: "Cofrinhos",
  account_sharing: "Compartilhamento",
  max_shared_users: "Usu\u00e1rios compartilhados",
  dre_reports: "Relat\u00f3rios DRE",
  ai_spending_analysis: "An\u00e1lise inteligente",
  recurring_transactions: "Transa\u00e7\u00f5es recorrentes",
  reports_access: "Relat\u00f3rios",
};

const FEATURE_ORDER = [
  "max_checking_accounts",
  "max_savings_accounts",
  "max_credit_cards",
  "credit_card_access",
  "statement_import",
  "budget_planning",
  "provisions",
  "max_goals",
  "account_sharing",
  "max_shared_users",
  "dre_reports",
  "ai_spending_analysis",
  "recurring_transactions",
  "reports_access",
];

const TIER_ORDER: Record<string, number> = { freemium: 0, starter: 1, colab: 2 };

function formatPrice(priceCents: number): string {
  const reais = priceCents / 100;
  return reais.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

function formatFeatureValue(feature: PlanFeature): React.ReactNode {
  if (!feature.enabled) {
    return <X className="h-4 w-4 text-muted-foreground/50" />;
  }

  if (feature.limit_value === null) {
    if (
      feature.feature_key.startsWith("max_") ||
      feature.feature_key === "max_shared_users"
    ) {
      return (
        <span className="text-sm font-medium text-primary">Ilimitado</span>
      );
    }
    return <Check className="h-4 w-4 text-primary" />;
  }

  if (feature.limit_value === 0) {
    return <X className="h-4 w-4 text-muted-foreground/50" />;
  }

  return (
    <span className="text-sm font-medium text-foreground">
      {feature.limit_value}
    </span>
  );
}

export function PricingSection() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from("plans")
        .select("*, plan_features(*)")
        .eq("is_active", true)
        .order("price_cents", { ascending: true });

      const FEATURES_TO_HIDE = [
        "max_savings_accounts",
        "max_credit_cards",
        "transfers_access",
        "statement_import",
        "provisions",
        "max_goals",
      ];

      if (error) {
        console.error("Error fetching plans:", error);
        return;
      }

      const sorted = (data as unknown as Plan[]).sort(
        (a, b) => (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99)
      );

      const filtered = sorted.map((plan) => ({
        ...plan,
        plan_features: plan.plan_features.filter(
          (f) => !FEATURES_TO_HIDE.includes(f.feature_key)
        ),
      }));

      setPlans(filtered);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <section id="pricing" className="py-20 bg-background">
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Escolha o plano ideal para voc&ecirc;
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Comece gr&aacute;tis e evolua conforme suas necessidades. Todos os
            planos pagos incluem 7 dias de teste gr&aacute;tis.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const isPopular = plan.tier === "starter";
            const isFree = plan.price_cents === 0;

            const featureMap = new Map(
              plan.plan_features.map((f) => [f.feature_key, f])
            );

            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col ${
                  isPopular
                    ? "border-primary shadow-lg scale-[1.02]"
                    : ""
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="px-4 py-1">Mais Popular</Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {plan.description}
                    </p>
                  )}

                  <div className="pt-4">
                    {isFree ? (
                      <>
                        <div className="text-4xl font-bold text-foreground">
                          Gr&aacute;tis
                        </div>
                        <Badge variant="primary" className="mt-2">
                          Pra sempre grátis
                        </Badge>
                      </>
                    ) : (
                      <>
                        <div className="text-4xl font-bold text-foreground">
                          {formatPrice(plan.price_cents)}
                          <span className="text-base font-normal text-muted-foreground">
                            /m&ecirc;s
                          </span>
                        </div>
                        {plan.trial_days > 0 && (
                          <Badge variant="secondary" className="mt-2">
                            {plan.trial_days} dias gr&aacute;tis
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 pt-4">
                  <ul className="space-y-3">
                    {FEATURE_ORDER.map((featureKey) => {
                      const feature = featureMap.get(featureKey);
                      if (!feature) return null;

                      return (
                        <li
                          key={featureKey}
                          className="flex items-center justify-between text-sm"
                        >
                          <span
                            className={
                              feature.enabled
                                ? "text-foreground"
                                : "text-muted-foreground/50"
                            }
                          >
                            {FEATURE_LABELS[featureKey] ?? featureKey}
                          </span>
                          {formatFeatureValue(feature)}
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>

                <CardFooter className="pt-4">
                  <Button
                    className="w-full"
                    variant={isPopular ? "default" : "outline"}
                    size="lg"
                    onClick={() => navigate("/auth?tab=signup")}
                  >
                    {isFree ? "Come\u00e7ar Gr\u00e1tis" : "Experimentar Gr\u00e1tis"}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
